<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { baseCodeApi } from '@/shared/api/baseCodeApi';
import { useSessionStore } from '@/shared/stores/sessionStore';
import type { FileUploadData, ColumnMapping, ValidatedRow } from '../types/contact.types';
import { INDIVIDUAL_SYSTEM_COLUMNS, CORPORATE_SYSTEM_COLUMNS } from '../types/contact.types';

interface Props {
    contactType: 'Individual' | 'Corporate';
    fileData: FileUploadData;
    mappings: ColumnMapping;
    validatedData: ValidatedRow[];
    currentStep: 1 | 2 | 3 | 4;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    'mapping-updated': [mappings: ColumnMapping];
    'validation-complete': [validatedData: ValidatedRow[]];
    'step-changed': [step: number];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);
const sessionStore = useSessionStore();

// 2단계: Field Mapping
// skipFirstLine: true면 첫 번째 행(컬럼 정보)을 건너뜀 (기본값: true)
// false면 모든 행을 표시 (첫 번째 행 포함)
const skipFirstLine = ref(true);
const autoColumnMapping = ref(false); // 자동 매핑 체크박스 상태
const localMappings = ref<ColumnMapping>({ ...props.mappings });
const selectedRows = ref<Set<number>>(new Set()); // 선택된 행 인덱스
const columnAssignments = ref<Record<string, string | null>>({});
const currentColumnPage = ref(1); // 시스템 컬럼 페이지 (1: 1-8, 2: 9-12)
const currentFileColumnPage = computed({
    get: () => currentColumnPage.value,
    set: (val) => { currentColumnPage.value = val; }
}); // 파일 컬럼 페이지를 시스템 컬럼 페이지와 동기화

const syncAssignmentsFromMappings = () => {
    const assignments: Record<string, string | null> = {};
    if (props.fileData && props.fileData.columns) {
        props.fileData.columns.forEach(col => {
            assignments[col] = null;
        });
    }
    Object.entries(localMappings.value).forEach(([systemKey, fileCol]) => {
        if (fileCol) {
            assignments[fileCol] = systemKey;
        }
    });
    columnAssignments.value = assignments;
};

watch(() => props.fileData?.columns, (newColumns) => {
    if (newColumns) {
        Object.keys(localMappings.value).forEach((key) => {
            const mapped = localMappings.value[key];
            if (mapped && !newColumns.includes(mapped)) {
                localMappings.value[key] = null;
            }
        });
    }
    syncAssignmentsFromMappings();
}, { immediate: true });

const systemColumns = computed(() => {
    return props.contactType === 'Individual' ? INDIVIDUAL_SYSTEM_COLUMNS : CORPORATE_SYSTEM_COLUMNS;
});

// 다음 시스템 컬럼 페이지가 있는지 확인
const hasNextColumnPage = computed(() => {
    return props.fileData && props.fileData.columns
        ? currentColumnPage.value * 8 < props.fileData.columns.length
        : false;
});

// 이전 시스템 컬럼 페이지가 있는지 확인
const hasPrevColumnPage = computed(() => {
    return currentColumnPage.value > 1;
});

// 드롭다운 아이템 생성 (시스템 컬럼 12개 고정)
const dropdownItems = computed(() => {
    const items: Array<{ title: string; value: string | null }> = [
        { title: 'Select a Column', value: null }
    ];
    // 시스템 컬럼 12개를 드롭다운 리스트로 표시 (고정 값)
    systemColumns.value.forEach((col) => {
        items.push({
            title: col.label + (col.required ? ' *' : ''),
            value: col.key // 시스템 컬럼 키를 value로 사용
        });
    });
    return items;
});

// 현재 페이지의 파일 컬럼 (8개씩) - 시스템 컬럼과 동기화
const currentFileColumns = computed(() => {
    // fileData가 없거나 columns가 없어도 빈 배열 반환 (컴포넌트가 사라지지 않도록)
    if (!props.fileData) {
        console.warn('currentFileColumns: fileData가 없습니다');
        return [];
    }
    if (!props.fileData.columns || props.fileData.columns.length === 0) {
        console.warn('currentFileColumns: columns가 없습니다', props.fileData);
        return [];
    }
    const startIndex = (currentColumnPage.value - 1) * 8;
    return props.fileData.columns.slice(startIndex, startIndex + 8);
});

// 시스템 컬럼 페이지 이동
const nextColumnPage = () => {
    if (hasNextColumnPage.value) {
        currentColumnPage.value++;
    }
};

const prevColumnPage = () => {
    if (hasPrevColumnPage.value) {
        currentColumnPage.value--;
    }
};

// 파일 컬럼 페이지 이동 (시스템 컬럼과 동기화)
const nextFileColumnPage = () => {
    if (hasNextColumnPage.value) {
        currentColumnPage.value++;
    }
};

const prevFileColumnPage = () => {
    if (hasPrevColumnPage.value) {
        currentColumnPage.value--;
    }
};

// 파일 컬럼 헤더 표시 (매핑된 시스템 컬럼 label 또는 Column1, Column2 형식)
const fileColumnHeaders = computed(() => {
    if (!props.fileData || !props.fileData.columns) {
        return [];
    }
    
    return props.fileData.columns.map((fileCol, index) => {
        // 먼저 매핑된 시스템 컬럼이 있는지 확인
        const selectedSystemKey = columnAssignments.value[fileCol];
        if (selectedSystemKey) {
            const mappedSystemCol = systemColumns.value.find(col => col.key === selectedSystemKey);
            if (mappedSystemCol) {
                return mappedSystemCol.label;
            }
        }
        
        // 매핑이 없으면 Column1, Column2 형식으로 표시
        return `Column${index + 1}`;
    });
});

// 포맷된 컬럼 헤더 (Validation 및 Review 단계에서 사용)
const formattedColumns = computed<string[]>(() => {
    return fileColumnHeaders.value;
});

// 미리보기 데이터 (모든 행 표시, 스크롤로 처리)
// skipFirstLine: true면 첫 번째 행(컬럼 정보)을 제외하고 표시, false면 모든 행 표시
const previewRows = computed(() => {
    // fileData가 없어도 빈 배열 반환 (컴포넌트가 사라지지 않도록)
    if (!props.fileData) {
        console.warn('previewRows: fileData가 없습니다');
        return [];
    }
    if (!props.fileData.rows) {
        console.warn('previewRows: rows가 없습니다', props.fileData);
        return [];
    }
    
    // skipFirstLine이 true면 첫 번째 행을 제외, false면 모든 행 표시
    const startIndex = skipFirstLine.value ? 1 : 0;
    const rows = props.fileData.rows.slice(startIndex);
    
    // 모든 행을 유지 (빈 행도 포함하여 columns 유지)
    // rows가 빈 배열이어도 빈 배열 반환 (컴포넌트가 사라지지 않도록)
    return rows.map((row, index) => ({
        ...row,
        _originalIndex: startIndex + index
    }));
});

// 행 선택/해제
const toggleRowSelection = (rowIndex: number) => {
    if (selectedRows.value.has(rowIndex)) {
        selectedRows.value.delete(rowIndex);
    } else {
        selectedRows.value.add(rowIndex);
    }
};

// 선택된 행 삭제
const deleteSelectedRows = () => {
    if (selectedRows.value.size === 0) return;
    
    console.log('deleteSelectedRows 호출:', {
        selectedCount: selectedRows.value.size,
        totalRows: props.fileData?.rows?.length,
        columnsCount: props.fileData?.columns?.length
    });
    
    // 원본 인덱스를 역순으로 정렬하여 삭제 (뒤에서부터 삭제해야 인덱스가 안 맞지 않음)
    const rowsToDelete = Array.from(selectedRows.value)
        .map(rowIndex => {
            const row = previewRows.value[rowIndex];
            if (!row) return null;
            return {
                previewIndex: rowIndex,
                originalIndex: row._originalIndex ?? rowIndex
            };
        })
        .filter(item => item !== null && item.originalIndex >= 0 && item.originalIndex < (props.fileData?.rows?.length || 0))
        .sort((a, b) => b!.originalIndex - a!.originalIndex) as Array<{ previewIndex: number; originalIndex: number }>;
    
    // 행 삭제 (데이터 행만 삭제, columns는 유지)
    rowsToDelete.forEach(item => {
        if (props.fileData && props.fileData.rows && item.originalIndex < props.fileData.rows.length) {
            props.fileData.rows.splice(item.originalIndex, 1);
        }
    });
    
    // selectedRows 초기화 (행 삭제 후 previewRows가 재계산되므로 인덱스가 바뀜)
    selectedRows.value.clear();
    
    console.log('deleteSelectedRows: 행 삭제 완료, 남은 행 수:', props.fileData?.rows?.length);
    console.log('deleteSelectedRows: columns 유지 확인:', props.fileData?.columns?.length);
    
    // columns가 유지되는지 확인
    if (!props.fileData?.columns || props.fileData.columns.length === 0) {
        console.error('deleteSelectedRows: columns가 사라졌습니다!', props.fileData);
    }
    
    // Validation이 실행된 경우 다시 실행하여 업데이트
    if (hasValidated.value) {
        runValidation();
    }
};


// 단일 행 삭제 (2단계에서 사용)
const deletePreviewRow = (rowIndex: number) => {
    if (!props.fileData) {
        console.error('deletePreviewRow: fileData가 없습니다!');
        return;
    }
    
    if (!props.fileData.rows || props.fileData.rows.length === 0) {
        console.warn('deletePreviewRow: rows가 비어있습니다.');
        return;
    }
    
    // previewRows에서 원본 인덱스 가져오기
    const row = previewRows.value[rowIndex];
    if (!row) {
        console.warn('deletePreviewRow: row not found at index', rowIndex);
        return;
    }
    
    const originalIndex = row._originalIndex ?? rowIndex;
    
    console.log('deletePreviewRow:', { 
        rowIndex, 
        originalIndex, 
        totalRows: props.fileData.rows.length,
        columnsCount: props.fileData.columns?.length,
        fileDataExists: !!props.fileData,
        columnsExists: !!props.fileData.columns
    });
    
    if (originalIndex >= 0 && originalIndex < props.fileData.rows.length) {
        // 행 삭제 (데이터 행만 삭제, columns는 유지)
        props.fileData.rows.splice(originalIndex, 1);
        
        // selectedRows 초기화 (행 삭제 후 previewRows가 재계산되므로 인덱스가 바뀜)
        selectedRows.value.clear();
        
        console.log('deletePreviewRow: row deleted, remaining rows:', props.fileData.rows.length);
        console.log('deletePreviewRow: columns 유지 확인:', props.fileData.columns?.length);
        console.log('deletePreviewRow: fileData 상태:', {
            fileDataExists: !!props.fileData,
            columnsExists: !!props.fileData.columns,
            columnsLength: props.fileData.columns?.length,
            rowsLength: props.fileData.rows.length
        });
        
        // columns가 유지되는지 확인
        if (!props.fileData.columns || props.fileData.columns.length === 0) {
            console.error('deletePreviewRow: columns가 사라졌습니다!', props.fileData);
        }
        
        // fileData가 null이 되었는지 확인
        if (!props.fileData) {
            console.error('deletePreviewRow: fileData가 null이 되었습니다!');
        }
    } else {
        console.warn('deletePreviewRow: invalid originalIndex', originalIndex);
    }
    
    if (hasValidated.value) {
        runValidation();
    }
};

// 셀 값 가져오기 (직접 접근 방식)
const getCellValueDirect = (row: any, col: string, colIndex: number, rowIndex?: number) => {
    if (!row) {
        console.warn('getCellValueDirect: row is null/undefined', { col, colIndex, rowIndex });
        return '';
    }
    
    // col로 직접 접근 (col은 currentFileColumns의 값, 즉 실제 컬럼명)
    if (col) {
        if (row.hasOwnProperty(col)) {
            const value = row[col];
            if (value !== undefined && value !== null) {
                return String(value);
            }
        }
    }
    
    // 전체 컬럼 배열에서 실제 컬럼명 찾기
    if (props.fileData && props.fileData.columns) {
        const globalColIndex = (currentColumnPage.value - 1) * 8 + colIndex;
        if (globalColIndex >= 0 && globalColIndex < props.fileData.columns.length) {
            const actualColName = props.fileData.columns[globalColIndex];
            if (actualColName && row.hasOwnProperty(actualColName)) {
                const value = row[actualColName];
                if (value !== undefined && value !== null) {
                    return String(value);
                }
            }
        }
    }
    
    return '';
};

// 셀 값 업데이트
const updateCellValue = (rowIndex: number, col: string, newValue: string) => {
    if (!props.fileData || !props.fileData.rows) return;
    
    const row = previewRows.value[rowIndex];
    if (!row) return;
    
    const originalIndex = row._originalIndex ?? rowIndex;
    if (originalIndex >= 0 && originalIndex < props.fileData.rows.length) {
        props.fileData.rows[originalIndex][col] = newValue;
        
        // Validation이 실행된 경우 다시 실행하여 업데이트
        if (hasValidated.value) {
            runValidation();
        }
    }
};

// RESET MAPPING
const handleResetMapping = () => {
    systemColumns.value.forEach(column => {
        localMappings.value[column.key] = null;
    });
    syncAssignmentsFromMappings();
    emit('mapping-updated', { ...localMappings.value });
};

// AUTO COLUMN MAPPING
const performAutoColumnMapping = () => {
    if (!props.fileData?.columns?.length) return;

    // 기존 매핑 초기화 후 파일 컬럼을 순서대로 시스템 컬럼에 매핑
    systemColumns.value.forEach((column) => {
        localMappings.value[column.key] = null;
    });

    props.fileData.columns.forEach((fileCol, idx) => {
        const targetSystemCol = systemColumns.value[idx];
        if (targetSystemCol) {
            localMappings.value[targetSystemCol.key] = fileCol;
        }
    });

    syncAssignmentsFromMappings();
    emit('mapping-updated', { ...localMappings.value });
};

const handleAutoColumnMappingChange = (value: boolean | null) => {
    const boolValue = value ?? false;
    autoColumnMapping.value = boolValue;
    if (boolValue) {
        performAutoColumnMapping();
    } else {
        handleResetMapping();
    }
};

// 매핑 업데이트 (파일 컬럼 -> 시스템 컬럼)
const updateColumnAssignment = (fileColumn: string, selectedSystemColKey: string | null) => {
    const currentAssignedKey = columnAssignments.value[fileColumn] || null;
    
    if (currentAssignedKey === selectedSystemColKey) {
        return;
    }
    
    // 현재 파일 컬럼에 연결된 매핑 제거
    if (currentAssignedKey && localMappings.value[currentAssignedKey] === fileColumn) {
        localMappings.value[currentAssignedKey] = null;
    }
    
    // 선택된 시스템 컬럼이 다른 파일 컬럼에 매핑되어 있으면 해제
    if (selectedSystemColKey) {
        Object.entries(columnAssignments.value).forEach(([col, assignedKey]) => {
            if (col !== fileColumn && assignedKey === selectedSystemColKey) {
                columnAssignments.value[col] = null;
            }
        });
        
        Object.keys(localMappings.value).forEach(systemKey => {
            if (systemKey === selectedSystemColKey) {
                localMappings.value[systemKey] = fileColumn;
            } else if (localMappings.value[systemKey] === fileColumn) {
                localMappings.value[systemKey] = null;
            }
        });
        
        if (!Object.prototype.hasOwnProperty.call(localMappings.value, selectedSystemColKey)) {
            localMappings.value[selectedSystemColKey] = fileColumn;
        }
        
        columnAssignments.value[fileColumn] = selectedSystemColKey;
    } else {
        columnAssignments.value[fileColumn] = null;
        Object.keys(localMappings.value).forEach(systemKey => {
            if (localMappings.value[systemKey] === fileColumn) {
                localMappings.value[systemKey] = null;
            }
        });
    }
    
    syncAssignmentsFromMappings();
    emit('mapping-updated', { ...localMappings.value });
};

// 필수 필드 매핑 확인
const checkRequiredMappings = computed(() => {
    const requiredColumns = systemColumns.value.filter(col => col.required);
    return requiredColumns.every(col => localMappings.value[col.key] !== null && localMappings.value[col.key] !== undefined);
});

// 3단계: Validation
const validatedRows = ref<ValidatedRow[]>([]);

// Identification Type 옵션 목록 (B03 메인코드)
const identificationTypeOptions = ref<Array<{ TOTAL_CODE: string; CODE_NAME: string }>>([]);

// Identification Type 옵션 로드
const loadIdentificationTypes = async () => {
    try {
        const response = await baseCodeApi.getCodesByMainCode('CM', 'B03', undefined);
        const codes = (response as any).codes || response.data?.codes || [];
        
        identificationTypeOptions.value = codes
            .map((code: any) => ({
                TOTAL_CODE: String(code.totalCode || code.TOTAL_CODE || '').trim(),
                CODE_NAME: String(code.defaultCodeName || code.DEFAULT_CODE_NAME || code.codeName || code.CODE_NAME || '').trim()
            }))
            .filter((item: any) => {
                return item.TOTAL_CODE && item.CODE_NAME && 
                       item.TOTAL_CODE.length > 0 && item.CODE_NAME.length > 0;
            });
    } catch (error) {
        console.error('Identification Type 로드 실패:', error);
        identificationTypeOptions.value = [];
    }
};

// 유효한 validatedRows만 필터링 (빈 데이터 제외)
// runValidation에서 이미 필터링하므로 여기서는 그대로 반환
const validValidatedRows = computed(() => {
    return validatedRows.value;
});

// 날짜 포맷 검증 함수 (dateUtils.ts의 parseDateString 로직 참고)
const validateDateFormat = (dateString: string, dateFormat: string): boolean => {
    if (!dateString || !dateFormat) return false;
    
    const format = dateFormat.toUpperCase();
    let year = '', month = '', day = '';
    
    // 구분자에 따라 파싱
    if (format.includes('/')) {
        const parts = dateString.split('/');
        if (parts.length !== 3) return false;
        if (format.startsWith('MM')) {
            // MM/DD/YYYY
            month = parts[0];
            day = parts[1];
            year = parts[2];
        } else if (format.startsWith('DD')) {
            // DD/MM/YYYY
            day = parts[0];
            month = parts[1];
            year = parts[2];
        } else if (format.startsWith('YYYY')) {
            // YYYY/MM/DD
            year = parts[0];
            month = parts[1];
            day = parts[2];
        }
    } else if (format.includes('-')) {
        const parts = dateString.split('-');
        if (parts.length !== 3) return false;
        if (format.startsWith('YYYY')) {
            // YYYY-MM-DD
            year = parts[0];
            month = parts[1];
            day = parts[2];
        } else if (format.startsWith('DD')) {
            // DD-MM-YYYY
            day = parts[0];
            month = parts[1];
            year = parts[2];
        }
    } else if (format.includes('.')) {
        const parts = dateString.split('.');
        if (parts.length !== 3) return false;
        if (format.startsWith('YYYY')) {
            // YYYY.MM.DD
            year = parts[0];
            month = parts[1];
            day = parts[2];
        } else if (format.startsWith('MM')) {
            // MM.DD.YYYY
            month = parts[0];
            day = parts[1];
            year = parts[2];
        } else if (format.startsWith('DD')) {
            // DD.MM.YYYY
            day = parts[0];
            month = parts[1];
            year = parts[2];
        }
    }
    
    // YYYY-MM-DD 형식도 허용 (일반적인 ISO 형식)
    if (!year || !month || !day) {
        // YYYY-MM-DD 형식 직접 확인
        const isoMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (isoMatch) {
            year = isoMatch[1];
            month = isoMatch[2];
            day = isoMatch[3];
        } else {
            return false;
        }
    }
    
    // 유효한 날짜인지 확인
    const yearNum = parseInt(year, 10);
    const monthNum = parseInt(month, 10);
    const dayNum = parseInt(day, 10);
    
    if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) {
        return false;
    }
    
    // 월과 일 범위 확인
    if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
        return false;
    }
    
    // 실제 유효한 날짜인지 확인 (예: 2월 30일 같은 경우)
    const date = new Date(yearNum, monthNum - 1, dayNum);
    if (date.getFullYear() !== yearNum || date.getMonth() !== monthNum - 1 || date.getDate() !== dayNum) {
        return false;
    }
    
    return true;
};

// 필수값 체크
const checkRequiredFields = (rows: any[]): Array<{ index: number; reason: string }> => {
    const missingRequired: Array<{ index: number; reason: string }> = [];
    
    // 세션에서 dateFormat 가져오기
    const userDateFormat = sessionStore.user?.user?.dateFormat || 'DD MMM YYYY';
    // dateFormat에서 실제 파싱 가능한 형식 추출
    // 'DD MMM YYYY' 같은 형식은 'DD/MM/YYYY', 'DD-MM-YYYY', 'DD.MM.YYYY' 등으로 변환 가능
    let dateFormatPattern = userDateFormat.toUpperCase();
    // 'MMM' 또는 'MMMM' 같은 월 이름 형식을 'MM'으로 변환
    dateFormatPattern = dateFormatPattern.replace(/MMMM?/g, 'MM');
    // 공백 구분자를 슬래시로 변환 (일반적인 날짜 형식)
    if (dateFormatPattern.includes(' ')) {
        dateFormatPattern = dateFormatPattern.replace(/\s+/g, '/');
    }
    
    // 파일 컬럼을 가져오는 헬퍼 함수
    const getFileColumn = (mappingValue: string | null): string | null => {
        if (!mappingValue) return null;
        const isSystemColumnKey = systemColumns.value.some(col => col.key === mappingValue);
        if (isSystemColumnKey) {
            const mappedFileColumn = localMappings.value[mappingValue];
            if (mappedFileColumn && !systemColumns.value.some(col => col.key === mappedFileColumn)) {
                return mappedFileColumn;
            }
            return null;
        }
        return mappingValue;
    };
    
    rows.forEach((row, index) => {
        if (props.contactType === 'Corporate') {
            // Corporate: Entity Name, Registration Number 필수 체크
            const missingFields: string[] = [];
            
            // Entity Name 필수 체크
            let entityNameMapping = localMappings.value['CONTACT_NAME'];
            entityNameMapping = getFileColumn(entityNameMapping);
            if (entityNameMapping && (!row[entityNameMapping] || String(row[entityNameMapping]).trim() === '')) {
                missingFields.push('Entity Name');
            }
            
            // Registration Number 필수 체크
            let regNoMapping = localMappings.value['CONTACT_REG_NO'];
            regNoMapping = getFileColumn(regNoMapping);
            if (regNoMapping && (!row[regNoMapping] || String(row[regNoMapping]).trim() === '')) {
                missingFields.push('Registration Number');
            }
            
            // 필수 필드가 하나라도 누락된 경우
            if (missingFields.length > 0) {
                missingRequired.push({
                    index,
                    reason: `Required field missing: ${missingFields.join(', ')}`
                });
            }
            
            // Date of Incorporation 날짜 포맷 검증
            let dateOfIncorporationMapping = localMappings.value['DATE_OF_INCORPORATION'];
            dateOfIncorporationMapping = getFileColumn(dateOfIncorporationMapping);
            if (dateOfIncorporationMapping && row[dateOfIncorporationMapping] && String(row[dateOfIncorporationMapping]).trim() !== '') {
                const dateValue = String(row[dateOfIncorporationMapping]).trim();
                // 여러 구분자 형식 시도 (/, -, .)
                const formatsToTry = [
                    dateFormatPattern,
                    dateFormatPattern.replace(/\//g, '-'),
                    dateFormatPattern.replace(/\//g, '.'),
                    dateFormatPattern.replace(/-/g, '/'),
                    dateFormatPattern.replace(/\./g, '/'),
                    'YYYY-MM-DD' // ISO 형식도 허용
                ];
                
                const isValidDate = formatsToTry.some(format => {
                    // 날짜 문자열의 구분자도 포맷에 맞게 변경
                    let testDateValue = dateValue;
                    if (format.includes('/') && !dateValue.includes('/')) {
                        testDateValue = dateValue.replace(/[-.]/g, '/');
                    } else if (format.includes('-') && !dateValue.includes('-')) {
                        testDateValue = dateValue.replace(/[.\/]/g, '-');
                    } else if (format.includes('.') && !dateValue.includes('.')) {
                        testDateValue = dateValue.replace(/[-\/]/g, '.');
                    }
                    return validateDateFormat(testDateValue, format);
                });
                
                if (!isValidDate) {
                    missingRequired.push({
                        index,
                        reason: `Date of Incorporation: "${dateValue}" does not match the expected date format (${userDateFormat})`
                    });
                }
            }
        } else {
            // Individual: Identification No, First Name, Last Name, Email 필수 체크
            const missingFields: string[] = [];
            
            // Identification No 필수 체크
            let idNoMapping = localMappings.value['CONTACT_REG_NO'];
            idNoMapping = getFileColumn(idNoMapping);
            if (idNoMapping && (!row[idNoMapping] || String(row[idNoMapping]).trim() === '')) {
                missingFields.push('Identification No');
            }
            
            // First Name 필수 체크
            let firstNameMapping = localMappings.value['FIRST_NAME'];
            firstNameMapping = getFileColumn(firstNameMapping);
            if (firstNameMapping && (!row[firstNameMapping] || String(row[firstNameMapping]).trim() === '')) {
                missingFields.push('First Name');
            }
            
            // Last Name 필수 체크
            let surnameMapping = localMappings.value['SURNAME'];
            surnameMapping = getFileColumn(surnameMapping);
            if (surnameMapping && (!row[surnameMapping] || String(row[surnameMapping]).trim() === '')) {
                missingFields.push('Last Name');
            }
            
            // Email 필수 체크
            let emailMapping = localMappings.value['CONTACT_EMAIL'];
            emailMapping = getFileColumn(emailMapping);
            if (emailMapping && (!row[emailMapping] || String(row[emailMapping]).trim() === '')) {
                missingFields.push('Email');
            }
            
            // 필수 필드가 하나라도 누락된 경우
            if (missingFields.length > 0) {
                missingRequired.push({
                    index,
                    reason: `Required field missing: ${missingFields.join(', ')}`
                });
            }
            
            // Identification Type CODE_NAME 검증 (매핑되어 있고 값이 있을 때만)
            let idTypeMapping = localMappings.value['ID_TYPE_CD'];
            idTypeMapping = getFileColumn(idTypeMapping);
            if (idTypeMapping && row[idTypeMapping] && String(row[idTypeMapping]).trim() !== '') {
                const idTypeValue = String(row[idTypeMapping]).trim();
                
                // CODE_NAME 목록에 있는지 확인 (대소문자 무시, 공백 정규화)
                const normalizeForComparison = (str: string) => {
                    return str.toLowerCase().trim();
                };
                
                const isValidCodeName = identificationTypeOptions.value.some((option) => {
                    const codeName = normalizeForComparison(option.CODE_NAME || '');
                    const value = normalizeForComparison(idTypeValue);
                    // 정확히 일치하거나, 괄호 내부 공백 차이를 고려한 비교
                    return codeName === value || 
                           codeName.replace(/\s*\(/g, '(').replace(/\)\s*/g, ')') === value.replace(/\s*\(/g, '(').replace(/\)\s*/g, ')');
                });
                
                // TOTAL_CODE로 이미 입력된 경우도 허용
                const isValidTotalCode = identificationTypeOptions.value.some((option) => {
                    const totalCode = (option.TOTAL_CODE || '').trim();
                    return totalCode === idTypeValue;
                });
                
                if (!isValidCodeName && !isValidTotalCode) {
                    missingRequired.push({
                        index,
                        reason: `Identification Type: "${idTypeValue}" is not a valid code`
                    });
                }
            }
            
            // Date of Birth 날짜 포맷 검증
            let dateOfBirthMapping = localMappings.value['DATE_OF_BIRTH'];
            dateOfBirthMapping = getFileColumn(dateOfBirthMapping);
            if (dateOfBirthMapping && row[dateOfBirthMapping] && String(row[dateOfBirthMapping]).trim() !== '') {
                const dateValue = String(row[dateOfBirthMapping]).trim();
                // 여러 구분자 형식 시도 (/, -, .)
                const formatsToTry = [
                    dateFormatPattern,
                    dateFormatPattern.replace(/\//g, '-'),
                    dateFormatPattern.replace(/\//g, '.'),
                    dateFormatPattern.replace(/-/g, '/'),
                    dateFormatPattern.replace(/\./g, '/'),
                    'YYYY-MM-DD' // ISO 형식도 허용
                ];
                
                const isValidDate = formatsToTry.some(format => {
                    // 날짜 문자열의 구분자도 포맷에 맞게 변경
                    let testDateValue = dateValue;
                    if (format.includes('/') && !dateValue.includes('/')) {
                        testDateValue = dateValue.replace(/[-.]/g, '/');
                    } else if (format.includes('-') && !dateValue.includes('-')) {
                        testDateValue = dateValue.replace(/[.\/]/g, '-');
                    } else if (format.includes('.') && !dateValue.includes('.')) {
                        testDateValue = dateValue.replace(/[-\/]/g, '.');
                    }
                    return validateDateFormat(testDateValue, format);
                });
                
                if (!isValidDate) {
                    missingRequired.push({
                        index,
                        reason: `Date of Birth: "${dateValue}" does not match the expected date format (${userDateFormat})`
                    });
                }
            }
        }
    });
    
    return missingRequired;
};

// 엑셀 파일 내부 중복 체크
const checkDuplicatesInFile = (rows: any[]): Array<{ index: number; reason: string }> => {
    const duplicates: Array<{ index: number; reason: string }> = [];
    
        if (props.contactType === 'Individual') {
        // 이름 + 이메일 조합 중복 체크
        let firstNameMapping = localMappings.value['FIRST_NAME'];
        let surnameMapping = localMappings.value['SURNAME'];
        let emailMapping = localMappings.value['CONTACT_EMAIL'];
        
        // 시스템 컬럼 키인 경우, 실제 파일 컬럼을 찾기
        const getFileColumn = (mappingValue: string | null): string | null => {
            if (!mappingValue) return null;
            const isSystemColumnKey = systemColumns.value.some(col => col.key === mappingValue);
            if (isSystemColumnKey) {
                const mappedFileColumn = localMappings.value[mappingValue];
                if (mappedFileColumn && !systemColumns.value.some(col => col.key === mappedFileColumn)) {
                    return mappedFileColumn;
                }
                return null;
            }
            return mappingValue;
        };
        
        firstNameMapping = getFileColumn(firstNameMapping);
        surnameMapping = getFileColumn(surnameMapping);
        emailMapping = getFileColumn(emailMapping);
        
        if (firstNameMapping && surnameMapping && emailMapping) {
            const nameEmailMap = new Map<string, number[]>();
            rows.forEach((row, index) => {
                const firstName = String(row[firstNameMapping!] || '').trim();
                const surname = String(row[surnameMapping!] || '').trim();
                const email = String(row[emailMapping!] || '').trim();
                const key = `${firstName}::${surname}::${email}`;
                
                if (firstName && surname && email) {
                    if (!nameEmailMap.has(key)) {
                        nameEmailMap.set(key, []);
                    }
                    nameEmailMap.get(key)!.push(index);
                }
            });
            
            nameEmailMap.forEach((indices, key) => {
                if (indices.length > 1) {
                    indices.forEach(index => {
                        duplicates.push({
                            index,
                            reason: `Duplicate: Same name and email found in row ${indices[0] + 1}`
                        });
                    });
                }
            });
        }
        
        // Identification No 중복 체크
        let idNoMapping = localMappings.value['CONTACT_REG_NO'];
        idNoMapping = getFileColumn(idNoMapping);
        if (idNoMapping) {
            const idNoMap = new Map<string, number[]>();
            rows.forEach((row, index) => {
                const idNo = String(row[idNoMapping!] || '').trim();
                if (idNo) {
                    if (!idNoMap.has(idNo)) {
                        idNoMap.set(idNo, []);
                    }
                    idNoMap.get(idNo)!.push(index);
                }
            });
            
            idNoMap.forEach((indices, idNo) => {
                if (indices.length > 1) {
                    indices.forEach(index => {
                        duplicates.push({
                            index,
                            reason: `Duplicate: Same Identification No found in row ${indices[0] + 1}`
                        });
                    });
                }
            });
        }
    } else {
        // Corporate: Registration Number 중복 체크
        let regNoMapping = localMappings.value['CONTACT_REG_NO'];
        const getFileColumn = (mappingValue: string | null): string | null => {
            if (!mappingValue) return null;
            const isSystemColumnKey = systemColumns.value.some(col => col.key === mappingValue);
            if (isSystemColumnKey) {
                const mappedFileColumn = localMappings.value[mappingValue];
                if (mappedFileColumn && !systemColumns.value.some(col => col.key === mappedFileColumn)) {
                    return mappedFileColumn;
                }
                return null;
            }
            return mappingValue;
        };
        regNoMapping = getFileColumn(regNoMapping);
        if (regNoMapping) {
            const regNoMap = new Map<string, number[]>();
            rows.forEach((row, index) => {
                const regNo = String(row[regNoMapping!] || '').trim();
                if (regNo) {
                    if (!regNoMap.has(regNo)) {
                        regNoMap.set(regNo, []);
                    }
                    regNoMap.get(regNo)!.push(index);
                }
            });
            
            regNoMap.forEach((indices, regNo) => {
                if (indices.length > 1) {
                    indices.forEach(index => {
                        duplicates.push({
                            index,
                            reason: `Duplicate: Same Registration Number found in row ${indices[0] + 1}`
                        });
                    });
                }
            });
        }
    }
    
    return duplicates;
};

// Validation 실행
const runValidation = () => {
    // skipFirstLine이 true면 첫 번째 행(컬럼 정보)을 건너뜀
    // skipFirstLine이 false면 모든 행을 사용 (첫 번째 행 포함)
    const startIndex = skipFirstLine.value ? 1 : 0;
    const dataRows = props.fileData.rows.slice(startIndex);
    
    // 필수값 체크
    const missingRequired = checkRequiredFields(dataRows);
    
    // 중복 체크
    const duplicates = checkDuplicatesInFile(dataRows);
    
    // ValidatedRow 생성
    const validated: ValidatedRow[] = dataRows.map((row, index) => {
        const isDuplicate = duplicates.some(d => d.index === index);
        const isMissingRequired = missingRequired.some(m => m.index === index);
        
        // 매핑된 데이터 생성
        const mappedData: Record<string, any> = {};
        systemColumns.value.forEach(column => {
            const mappingValue = localMappings.value[column.key];
            if (mappingValue) {
                // mappingValue가 시스템 컬럼 키인지 파일 컬럼인지 확인
                const isSystemColumnKey = systemColumns.value.some(col => col.key === mappingValue);
                if (isSystemColumnKey) {
                    // 시스템 컬럼 키인 경우, 해당 시스템 컬럼이 매핑된 파일 컬럼을 찾아서 사용
                    const mappedFileColumn = localMappings.value[mappingValue];
                    if (mappedFileColumn && !systemColumns.value.some(col => col.key === mappedFileColumn)) {
                        // mappedFileColumn이 파일 컬럼인 경우
                        mappedData[column.key] = row[mappedFileColumn];
                    }
                } else {
                    // 파일 컬럼인 경우
                    mappedData[column.key] = row[mappingValue];
                }
            }
        });
        
        return {
            index,
            data: mappedData,
            isDuplicate,
            duplicateReason: isDuplicate ? duplicates.find(d => d.index === index)?.reason : undefined,
            isMissingRequired,
            missingRequiredReason: isMissingRequired ? missingRequired.find(m => m.index === index)?.reason : undefined
        };
    });
    
    // 빈 데이터 행 제거 (하지만 모든 값이 빈 경우도 행 자체는 유지)
    // 실제로는 모든 행을 유지하되, 빈 값은 빈 문자열로 표시
    // validatedRows를 직접 할당하여 Vue 반응성 보장
    validatedRows.value = [...validated];
    
    console.log('runValidation 완료:', {
        validatedCount: validated.length,
        validatedRowsLength: validatedRows.value.length,
        hasDuplicates: validated.some(r => r.isDuplicate),
        hasMissingRequired: validated.some(r => r.isMissingRequired),
        dataRowsCount: dataRows.length,
        fileDataRowsCount: props.fileData.rows.length,
        startIndex: startIndex,
        validated: validated.map((r, idx) => ({ 
            index: r.index, 
            dataKeys: Object.keys(r.data), 
            dataValues: Object.values(r.data).slice(0, 3), // 처음 3개 값만
            hasData: Object.keys(r.data).length > 0 
        }))
    });
    
    // 부모 컴포넌트에 validation 완료 알림
    emit('validation-complete', validated);
};

// 행 삭제 (3단계에서 사용)
const deleteRow = async (validatedIndex: number) => {
    console.log('deleteRow 호출:', { 
        validatedIndex, 
        totalValidatedRows: validatedRows.value.length,
        validatedRows: validatedRows.value.map(r => ({ index: r.index, dataKeys: Object.keys(r.data) }))
    });
    
    // validatedRow의 index는 dataRows 기준이므로, 실제 fileData.rows 인덱스로 변환
    const startIndex = skipFirstLine.value ? 1 : 0;
    const actualIndex = validatedIndex + startIndex;
    
    console.log('deleteRow 인덱스 변환:', { 
        validatedIndex, 
        startIndex, 
        actualIndex, 
        totalRows: props.fileData?.rows?.length,
        fileDataRows: props.fileData?.rows?.map((r, idx) => ({ idx, keys: Object.keys(r || {}) }))
    });
    
    if (props.fileData && props.fileData.rows && actualIndex >= 0 && actualIndex < props.fileData.rows.length) {
        // 실제 데이터에서 행 삭제
        const deletedRow = props.fileData.rows[actualIndex];
        console.log('deleteRow: 삭제할 행:', deletedRow);
        props.fileData.rows.splice(actualIndex, 1);
        
        console.log('deleteRow: 행 삭제 완료, 남은 행 수:', props.fileData.rows.length);
        console.log('deleteRow: 남은 행들:', props.fileData.rows.map((r, idx) => ({ idx, keys: Object.keys(r || {}) })));
        
        // Validation 다시 실행하여 validatedRows 업데이트
        if (hasValidated.value) {
            console.log('deleteRow: runValidation 호출');
            runValidation();
            console.log('deleteRow: runValidation 완료, validatedRows:', validatedRows.value.length);
            
            // Vue 반응성 보장을 위해 nextTick 사용
            await nextTick();
            console.log('deleteRow: nextTick 후 validatedRows:', validatedRows.value.length);
        }
    } else {
        console.warn('deleteRow: 유효하지 않은 인덱스', { actualIndex, totalRows: props.fileData?.rows?.length });
    }
};

// 매핑 변경 감지
watch(() => props.mappings, (newMappings) => {
    localMappings.value = { ...newMappings };
    syncAssignmentsFromMappings();
}, { deep: true });

// 2단계에서 Next 클릭 시 Validation 실행 (2단계 유지)
const hasValidated = ref(false);

// 컬럼 너비 스타일 computed 속성
// 체크박스 컬럼(30px) + Column1 너비 = Column2 너비
// 체크박스 컬럼(30px) + Column9 너비 = Column2 너비
// Column2~8, Column10~12는 모두 Column2와 동일한 너비
// Column1, Column9는 Column2보다 30px 좁은 너비
// 계산: Column2 너비 = 100% / 8, Column1 너비 = (100% / 8) - 30px
const columnWidthStyle = computed(() => {
    // 일반 컬럼(Column2~8, Column10~12): 100% / 8
    return {
        width: 'calc(100% / 8)',
        minWidth: 'calc(100% / 8)',
        maxWidth: 'calc(100% / 8)'
    };
});

// Column1, Column9 너비 (Column2보다 30px 좁음)
const firstColumnWidthStyle = computed(() => {
    // Column1, Column9: (100% / 8) - 30px
    return {
        width: 'calc(100% / 8 - 30px)',
        minWidth: 'calc(100% / 8 - 30px)',
        maxWidth: 'calc(100% / 8 - 30px)'
    };
});

// Next 버튼 클릭 시 Validation 실행 (2단계 유지)
const handleNextClick = () => {
    if (props.currentStep === 2 && checkRequiredMappings.value) {
        runValidation();
        hasValidated.value = true;
        // 2단계 화면 유지, Validation 결과만 표시
    }
};

watch(() => props.currentStep, (newStep) => {
    if (newStep === 3) {
        runValidation();
        hasValidated.value = true;
    }
});

// 초기 Validation 실행
if (props.currentStep >= 3) {
    runValidation();
    hasValidated.value = true;
}

// 컴포넌트 마운트 시 Identification Type 옵션 로드
onMounted(async () => {
    await loadIdentificationTypes();
});

// Validation 결과를 row 인덱스로 매핑하는 함수
const getRowValidation = (rowIndex: number) => {
    if (!hasValidated.value || !validatedRows.value.length) return null;
    
    // previewRows의 rowIndex를 실제 데이터 인덱스로 변환
    const row = previewRows.value[rowIndex];
    if (!row) return null;
    
    const originalIndex = row._originalIndex ?? rowIndex;
    
    // validatedRows는 skipFirstLine을 고려해서 생성되므로, originalIndex에서 startIndex를 빼야 함
    const startIndex = skipFirstLine.value ? 1 : 0;
    const actualIndex = originalIndex - startIndex;
    
    if (actualIndex >= 0 && actualIndex < validatedRows.value.length) {
        return validatedRows.value[actualIndex];
    }
    return null;
};

// 파일 컬럼명을 시스템 컬럼 키로 변환하는 헬퍼 함수
const getSystemColumnKeyFromFileColumn = (fileColumn: string): string | null => {
    // localMappings에서 해당 파일 컬럼에 매핑된 시스템 컬럼 키 찾기
    for (const [systemKey, mappedFileColumn] of Object.entries(localMappings.value)) {
        if (mappedFileColumn === fileColumn) {
            return systemKey;
        }
    }
    return null;
};

// Validation된 셀 값 업데이트
const updateValidatedCellValue = (validatedIndex: number, col: string, newValue: string) => {
    if (!props.fileData || !props.fileData.rows) return;
    
    const validatedRow = validatedRows.value.find(r => r.index === validatedIndex);
    if (!validatedRow) return;
    
    // validatedRow의 index는 dataRows 기준이므로, 실제 fileData.rows 인덱스로 변환
    const startIndex = skipFirstLine.value ? 1 : 0;
    const actualIndex = validatedRow.index + startIndex;
    
    if (actualIndex >= 0 && actualIndex < props.fileData.rows.length) {
        // fileData.rows 업데이트 (col은 파일 컬럼명)
        props.fileData.rows[actualIndex][col] = newValue;
        
        // validatedRow.data는 runValidation()에서 다시 생성되므로 여기서 직접 업데이트하지 않음
        // runValidation()이 호출되면 fileData.rows의 변경사항이 반영되어 validatedRow.data가 재생성됨
        
        // Validation 다시 실행하여 업데이트
        runValidation();
    }
};

// Validation된 데이터에서 파일 컬럼명으로 값 가져오기
const getValidatedValueByFileColumn = (validatedRow: ValidatedRow, fileColumn: string): any => {
    // 파일 컬럼명을 시스템 컬럼 키로 변환
    const systemKey = getSystemColumnKeyFromFileColumn(fileColumn);
    if (systemKey && validatedRow.data) {
        return validatedRow.data[systemKey];
    }
    return undefined;
};

// 부모 컴포넌트에서 Next 버튼 클릭 시 호출할 수 있도록 expose
defineExpose({
    handleNextClick
});
</script>

<template>
    <div class="content-section">
        <!-- 2단계: Field Mapping -->
        <div v-if="currentStep === 2" class="field-mapping-section">
            <!-- 상단 컨트롤 -->
            <div class="d-flex justify-space-between align-center mb-4">
                <span class="text-body-1">
                    <template v-if="!hasValidated || (props.validatedData && props.validatedData.length > 0 && props.validatedData.some((r: any) => r.isDuplicate || r.isMissingRequired))">
                    Please select the label column that correspond to your file column
                    </template>
                    <template v-else-if="hasValidated && props.validatedData && props.validatedData.length > 0">
                        Please proceed with the upload as there are no issues
                    </template>
                    <template v-else>
                        Please select the label column that correspond to your file column
                    </template>
                </span>
                <div class="d-flex align-center gap-3">
                    <v-checkbox
                        v-model="autoColumnMapping"
                        label="Auto Column Mapping"
                        hide-details
                        density="compact"
                        @update:model-value="handleAutoColumnMappingChange"
                    >
                        <template v-slot:append>
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props: tooltipProps }">
                                    <v-icon
                                        v-bind="tooltipProps"
                                        size="small"
                                        color="info"
                                        class="ml-1"
                                    >
                                        mdi-information
                                    </v-icon>
                                </template>
                                <span>Automatically map file columns to system columns in order</span>
                            </v-tooltip>
                        </template>
                    </v-checkbox>
                    <v-checkbox
                        v-model="skipFirstLine"
                        label="Skip the first line"
                        hide-details
                        density="compact"
                    >
                        <template v-slot:append>
                            <v-tooltip location="top">
                                <template v-slot:activator="{ props: tooltipProps }">
                                    <v-icon
                                        v-bind="tooltipProps"
                                        size="small"
                                        color="info"
                                        class="ml-1"
                                    >
                                        mdi-information
                                    </v-icon>
                                </template>
                                <span>Skip the first data row when processing. If checked, the first data row (row 2 in your file) will be skipped.</span>
                            </v-tooltip>
                        </template>
                    </v-checkbox>
                    <v-btn
                        variant="outlined"
                        color="primary"
                        @click="handleResetMapping"
                    >
                        RESET MAPPING
                    </v-btn>
                </div>
            </div>
            
            <!-- 시스템 컬럼 매핑과 파일 컬럼을 함께 표시 (위아래 배치) -->
            <div class="columns-mapping-container">
                <!-- 위쪽: 시스템 컬럼 -->
                <div class="columns-section">
                    <div class="section-header pa-2 mb-2">
                    <span>Columns</span>
                    </div>
                <div class="d-flex align-center columns-mapping-row">
                        <template v-for="(fileColumn, index) in currentFileColumns" :key="`${fileColumn}-${index}`">
                        <div class="d-flex flex-column align-center column-dropdown-wrapper">
                            <v-select
                                    :model-value="columnAssignments[fileColumn] || null"
                                :items="dropdownItems"
                                variant="outlined"
                                density="compact"
                                    hide-details
                                    placeholder="Select a Column"
                                    :class="{ 'selected-column': columnAssignments[fileColumn] }"
                                    @update:model-value="(val: any) => updateColumnAssignment(fileColumn, val)"
                                class="system-column-select"
                            />
                            <v-icon class="mt-1" color="primary">mdi-arrow-down</v-icon>
                        </div>
                    </template>
                    <!-- 빈 드롭다운 공간 (8개가 안 될 경우) -->
                        <template v-for="n in Math.max(0, 8 - currentFileColumns.length)" :key="`empty-col-${n}`">
                        <div class="d-flex flex-column align-center column-dropdown-wrapper">
                            <div class="system-column-select empty-dropdown"></div>
                            <v-icon class="mt-1" color="transparent">mdi-arrow-down</v-icon>
                        </div>
                    </template>
                </div>
            </div>
            
                <!-- 아래쪽: 파일 컬럼 -->
            <div class="file-columns-section">
                    <div class="section-header pa-2 mb-2 d-flex align-center">
                        <span class="flex-grow-0">Your file Columns</span>
                        <v-spacer></v-spacer>
                        <!-- Delete Selected 버튼 -->
                        <v-btn
                            v-if="selectedRows.size > 0"
                            color="error"
                            variant="outlined"
                            size="small"
                            prepend-icon="mdi-delete"
                            @click="deleteSelectedRows"
                            class="mr-2"
                        >
                            Delete Selected ({{ selectedRows.size }})
                        </v-btn>
                        <!-- 좌측: 이전 페이지 버튼 -->
                        <div class="d-flex align-center">
                        <v-btn
                                v-if="hasPrevColumnPage"
                                variant="flat"
                                color="success"
                            size="small"
                                @click="prevColumnPage"
                                class="square-arrow-button"
                                min-width="40"
                                height="32"
                        >
                                <v-icon size="20">mdi-chevron-left</v-icon>
                        </v-btn>
                        </div>
                        <!-- 우측: 다음 페이지 버튼 -->
                        <div class="d-flex align-center">
                        <v-btn
                                v-if="hasNextColumnPage"
                                variant="flat"
                                color="success"
                            size="small"
                                @click="nextColumnPage"
                                class="square-arrow-button"
                                min-width="40"
                                height="32"
                        >
                                <v-icon size="20">mdi-chevron-right</v-icon>
                        </v-btn>
                    </div>
                </div>
                    <div class="file-columns-table-wrapper">
                        <v-table class="file-columns-table" :class="{ 'has-validation': hasValidated }">
                            <thead class="file-columns-table-header">
                                <tr>
                                    <th class="text-center file-column-header checkbox-header" style="width: 30px;">
                                        <v-checkbox
                                            :model-value="selectedRows.size === previewRows.length && previewRows.length > 0"
                                            :indeterminate="selectedRows.size > 0 && selectedRows.size < previewRows.length"
                                            @update:model-value="(val) => {
                                                if (val) {
                                                    previewRows.forEach((_, idx) => selectedRows.add(idx));
                                                } else {
                                                    selectedRows.clear();
                                                }
                                            }"
                                            density="compact"
                                            hide-details
                                        />
                                    </th>
                                    <th
                                        v-for="(col, index) in currentFileColumns"
                                        :key="index"
                                        class="text-center file-column-header"
                                        :style="index === 0 ? firstColumnWidthStyle : columnWidthStyle"
                                    >
                                        {{ fileColumnHeaders[(currentColumnPage - 1) * 8 + index] || `Column${(currentColumnPage - 1) * 8 + index + 1}` }}
                                    </th>
                                    <!-- 8개가 안 될 경우 빈 셀 추가 -->
                                    <th
                                        v-for="n in Math.max(0, 8 - currentFileColumns.length)"
                                        :key="`empty-${n}`"
                                        class="text-center file-column-header"
                                        :style="currentFileColumns.length === 0 && n === 0 ? firstColumnWidthStyle : columnWidthStyle"
                                    ></th>
                                    <!-- Validation 헤더 -->
                                    <th 
                                        v-if="hasValidated"
                                        class="text-center file-column-header validation-header"
                                        style="width: 100px; min-width: 100px; max-width: 100px;"
                                    >
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="file-columns-table-body">
                                <tr 
                                    v-for="(row, rowIndex) in previewRows" 
                                    :key="rowIndex"
                                    :class="{ 
                                        'row-selected': selectedRows.has(rowIndex),
                                        'row-error': hasValidated && getRowValidation(rowIndex) && (getRowValidation(rowIndex)?.isDuplicate || getRowValidation(rowIndex)?.isMissingRequired)
                                    }"
                                    class="file-row"
                                >
                                    <td 
                                        class="text-center file-column-cell checkbox-cell"
                                    >
                                        <v-checkbox
                                            :model-value="selectedRows.has(rowIndex)"
                                            @update:model-value="() => toggleRowSelection(rowIndex)"
                                            density="compact"
                                            hide-details
                                        />
                                    </td>
                                    <td
                                        v-for="(col, colIndex) in currentFileColumns"
                                        :key="colIndex"
                                        class="text-center file-column-cell editable-cell"
                                        :style="colIndex === 0 ? firstColumnWidthStyle : columnWidthStyle"
                                    >
                                        <v-text-field
                                            :model-value="getCellValueDirect(row, col, colIndex, rowIndex)"
                                            @update:model-value="(val) => updateCellValue(rowIndex, col, val || '')"
                                            variant="plain"
                                            density="compact"
                                            hide-details
                                            class="cell-input"
                                        />
                                    </td>
                                    <!-- 8개가 안 될 경우 빈 셀 추가 -->
                                    <td
                                        v-for="n in Math.max(0, 8 - currentFileColumns.length)"
                                        :key="`empty-${n}`"
                                        class="text-center file-column-cell"
                                        :style="currentFileColumns.length === 0 && n === 0 ? firstColumnWidthStyle : columnWidthStyle"
                                    ></td>
                                    <!-- Validation 결과 표시 (툴팁과 휴지통) -->
                                    <td 
                                        v-if="hasValidated && getRowValidation(rowIndex)"
                                        class="text-center file-column-cell validation-cell"
                                    >
                                        <div class="d-flex align-center gap-2">
                                            <v-tooltip
                                                v-if="getRowValidation(rowIndex)?.isDuplicate || getRowValidation(rowIndex)?.isMissingRequired"
                                                location="top"
                                            >
                                                <template v-slot:activator="{ props: tooltipProps }">
                                                    <v-icon
                                                        v-bind="tooltipProps"
                                                        color="error"
                                                        size="small"
                                                    >
                                                        mdi-alert-circle
                                                    </v-icon>
                                                </template>
                                                <span>
                                                    {{ getRowValidation(rowIndex)?.isDuplicate ? getRowValidation(rowIndex)?.duplicateReason : getRowValidation(rowIndex)?.missingRequiredReason }}
                                                </span>
                                            </v-tooltip>
                                            <v-btn
                                                icon
                                                variant="text"
                                                size="small"
                                                color="error"
                                                @click="deletePreviewRow(rowIndex)"
                                            >
                                                <v-icon size="small">mdi-delete</v-icon>
                                            </v-btn>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </v-table>
                    </div>
                </div>
            </div>
        </div>
        
        <!-- 3단계: Validation -->
        <div v-if="currentStep === 3" class="validation-section">
            <h6 class="text-h6 mb-4">Step 3: Validation</h6>
            
            <v-table class="border rounded-md">
                <thead>
                    <tr>
                        <th
                            v-for="(col, index) in formattedColumns"
                            :key="index"
                            class="text-left"
                        >
                            {{ col }}
                        </th>
                        <th class="text-left">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(validatedRow, index) in validValidatedRows"
                        :key="`${validatedRow.index}-${index}`"
                        :class="{
                            'error-row': validatedRow.isDuplicate || validatedRow.isMissingRequired
                        }"
                    >
                        <td
                            v-for="(col, colIndex) in props.fileData.columns"
                            :key="colIndex"
                            :class="{
                                'error-cell': validatedRow.isDuplicate || validatedRow.isMissingRequired
                            }"
                            class="editable-cell"
                        >
                            <v-text-field
                                :model-value="(() => { const val = getValidatedValueByFileColumn(validatedRow, col); return val !== undefined && val !== null ? String(val) : ''; })()"
                                @update:model-value="(val) => updateValidatedCellValue(validatedRow.index, col, val || '')"
                                variant="plain"
                                density="compact"
                                hide-details
                                class="cell-input"
                            />
                        </td>
                        <td>
                            <v-tooltip
                                v-if="validatedRow.isDuplicate || validatedRow.isMissingRequired"
                                location="top"
                            >
                                <template v-slot:activator="{ props: tooltipProps }">
                                    <v-icon
                                        v-bind="tooltipProps"
                                        color="error"
                                        class="mr-2"
                                    >
                                        mdi-alert-circle
                                    </v-icon>
                                </template>
                                <span>
                                    {{ validatedRow.isDuplicate ? validatedRow.duplicateReason : validatedRow.missingRequiredReason }}
                                </span>
                            </v-tooltip>
                            <v-btn
                                icon
                                variant="text"
                                size="small"
                                color="error"
                                @click="deleteRow(validatedRow.index)"
                            >
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </tbody>
            </v-table>
        </div>
        
        <!-- 4단계: 최종 리뷰 -->
        <div v-if="currentStep === 4" class="review-section">
            <h6 class="text-h6 mb-4">Step 4: Final Review</h6>
            
            <v-card variant="outlined" class="mb-4">
                <v-card-text>
                    <div class="d-flex justify-space-between">
                        <div>
                            <div class="text-subtitle-2 mb-1">Total Rows</div>
                            <div class="text-h6">{{ validatedRows.length }}</div>
                        </div>
                        <div>
                            <div class="text-subtitle-2 mb-1">Valid Rows</div>
                            <div class="text-h6 text-success">
                                {{ validatedRows.filter(r => !r.isDuplicate && !r.isMissingRequired).length }}
                            </div>
                        </div>
                        <div>
                            <div class="text-subtitle-2 mb-1">Invalid Rows</div>
                            <div class="text-h6 text-error">
                                {{ validatedRows.filter(r => r.isDuplicate || r.isMissingRequired).length }}
                            </div>
                        </div>
                    </div>
                </v-card-text>
            </v-card>
            
            <v-table class="border rounded-md">
                <thead>
                    <tr>
                        <th
                            v-for="(col, index) in formattedColumns"
                            :key="index"
                            class="text-left"
                        >
                            {{ col }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(validatedRow, index) in validatedRows.filter(r => !r.isDuplicate && !r.isMissingRequired).slice(0, 10)"
                        :key="index"
                    >
                        <td
                            v-for="(col, colIndex) in props.fileData.columns"
                            :key="colIndex"
                            class="editable-cell"
                        >
                            <v-text-field
                                :model-value="(() => { const val = getValidatedValueByFileColumn(validatedRow, col); return val !== undefined && val !== null ? String(val) : ''; })()"
                                @update:model-value="(val) => updateValidatedCellValue(validatedRow.index, col, val || '')"
                                variant="plain"
                                density="compact"
                                hide-details
                                class="cell-input"
                            />
                        </td>
                    </tr>
                </tbody>
            </v-table>
            
            <p v-if="validatedRows.filter(r => !r.isDuplicate && !r.isMissingRequired).length > 10" class="text-caption mt-2">
                Showing first 10 rows. Total {{ validatedRows.filter(r => !r.isDuplicate && !r.isMissingRequired).length }} valid rows.
            </p>
        </div>
    </div>
</template>

<style scoped lang="scss">
.content-section {
    .section-header {
        background-color: rgba(var(--v-theme-surface-variant), 0.3);
        font-weight: 500;
    }
    
    .selected-column {
        :deep(.v-field) {
            background-color: rgba(var(--v-theme-primary), 0.1);
        }
    }
    
    .error-row {
        background-color: rgba(var(--v-theme-error), 0.1);
    }
    
    .error-cell {
        color: rgb(var(--v-theme-error));
    }
    
    .arrow-button {
        background-color: rgb(var(--v-theme-success)) !important;
        :deep(.v-icon) {
            color: white !important;
        }
    }
    
    .columns-mapping-container {
        width: 100%;
        display: flex;
        flex-direction: column;
        
        .columns-section,
        .file-columns-section {
            width: 100%;
        }
    }
    
    .columns-mapping-row {
        flex-wrap: nowrap;
        width: 100%;
        // 파일 컬럼 테이블과 정확히 일치하도록 gap 제거
        // gap이 있으면 flex 계산이 복잡해져서 정확히 일치하지 않을 수 있음
        gap: 0;
        // 파일 컬럼 테이블의 체크박스 컬럼 위치와 정렬하기 위해
        // 체크박스 컬럼의 실제 너비만큼 왼쪽 여백 추가
        // 체크박스 컬럼: width 30px (padding 제거하여 정확히 30px)
        // 파일 컬럼 테이블과 정확히 일치하도록 체크박스 컬럼 너비를 기준으로 계산
        // 파일 컬럼 테이블: 체크박스 30px + 파일 컬럼 8개 (각각 calc((100% - 30px) / 8))
        // 시스템 컬럼 드롭다운: 체크박스 위치(30px) + 파일 컬럼 8개 (각각 calc((100% - 30px) / 8))
        padding-left: 30px; // 체크박스 컬럼 너비만큼 왼쪽 여백
        box-sizing: border-box;
        
        .column-dropdown-wrapper {
            // 파일 컬럼 테이블의 컬럼 너비와 정확히 일치하도록 계산
            // 파일 컬럼 테이블: width = calc((100% - 30px) / 8) (체크박스 30px 제외)
            // 시스템 컬럼 드롭다운: padding-left: 30px를 추가했으므로, 
            // 실제 사용 가능한 너비는 100% - 30px (padding-left)
            // 따라서 각 드롭다운 너비: calc((100% - 30px) / 7.98) (약 0.3% 증가)
            flex: 0 0 calc((100% - 30px) / 7.98) !important; // 파일 컬럼 테이블보다 약간 넓게
            width: calc((100% - 30px) / 7.98) !important;
            min-width: calc((100% - 30px) / 7.98) !important;
            max-width: calc((100% - 30px) / 7.98) !important;
            box-sizing: border-box;
            
            // 드롭다운 사이의 간격을 margin으로 처리
            // 파일 컬럼 테이블의 체크박스 컬럼 너비(30px)를 7개의 간격으로 분배
            // 8개의 드롭다운 사이에 7개의 margin이 있고, 총 margin이 30px가 되도록 계산
            // 체크박스 너비(30px) / 7 = 약 4.29px
            &:not(:last-child) {
                margin-right: calc(30px / 7) !important;
            }
            
            // v-select와 빈 드롭다운 모두 동일한 너비를 가지도록
            .system-column-select {
                width: 100%;
                min-width: 0;
                max-width: 100%;
                box-sizing: border-box;
                
                :deep(.v-field) {
                    min-width: 0;
                    width: 100%;
                    max-width: 100%;
                    box-sizing: border-box;
                }
                
                :deep(.v-field__input) {
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    box-sizing: border-box;
                }
            }
            
            .empty-dropdown {
                height: 56px; // v-select와 동일한 높이
                width: 100%; // 부모의 너비를 100% 사용
                min-width: 0;
                max-width: 100%;
                border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
                border-radius: 4px;
                box-sizing: border-box; // border 포함 너비 계산
            }
        }
    }
    
    .file-columns-section {
        .file-columns-table-wrapper {
            max-height: 400px; // 최대 높이 설정
            overflow-y: auto; // 세로 스크롤
            overflow-x: hidden; // 가로 스크롤 숨김
            border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
            border-radius: 4px;
            // 테이블의 border와 padding을 고려하여 실제 사용 가능한 너비 계산
            // border-left + border-right = 2px
            // padding이 있다면 추가로 고려 필요
            box-sizing: border-box;
        }
        
        .square-arrow-button {
            border-radius: 4px !important;
            
            :deep(.v-icon) {
                color: white !important;
            }
        }
    }
    
    .file-columns-table {
        width: 100% !important;
        // table-layout: fixed로 모든 컬럼의 너비를 강제로 고정
        // 이렇게 하면 내용에 관계없이 모든 컬럼이 동일한 너비를 가짐
        // 헤더 텍스트가 변경되어도(시스템 컬럼 매핑 시) 컬럼 너비가 변하지 않도록 보장
        // table-layout: fixed를 사용하면 첫 번째 행(thead)의 셀 너비가 모든 행에 적용됨
        // 따라서 헤더 셀에 인라인 스타일로 설정한 너비가 모든 데이터 셀에도 적용됨
        table-layout: fixed !important; // 강제로 고정 레이아웃 사용
        // border-collapse를 사용하여 테이블의 border가 셀 너비에 영향을 주지 않도록 함
        border-collapse: collapse !important;
        // border는 wrapper에만 있고, 테이블 자체는 border가 없으므로
        // 실제 사용 가능한 너비는 wrapper의 100% (box-sizing: border-box로 border 포함)
        // 시스템 컬럼 드롭다운과 동일한 컨테이너 너비를 사용하므로
        // 파일 컬럼 테이블의 컬럼 너비를 기준으로 시스템 컬럼 드롭다운 너비를 계산
        // 모든 컬럼(빈 셀 포함)이 정확히 동일한 너비를 가지도록 보장
        // 헤더 텍스트가 변경되어도 컬럼 너비는 고정됨
        // 컬럼 9~12가 컬럼 1~8과 동일한 너비를 가지도록 보장
        
        .file-row {
            cursor: context-menu;
            
            &:hover {
                background-color: rgba(var(--v-theme-primary), 0.05);
            }
            
            &.row-selected {
                background-color: rgba(var(--v-theme-primary), 0.1);
            }
            
            &.row-error {
                background-color: rgba(var(--v-theme-error), 0.05);
            }
        }
        
        .validation-cell {
            width: 100px;
            padding: 8px !important;
        }
        
        .editable-cell {
            // 기본 padding 설정 (첫 번째 컬럼 제외)
            padding: 4px !important;
            // 컬럼 너비를 고정하고 내용이 넘치지 않도록 처리
            overflow: hidden !important;
            max-width: calc((100% - 30px) / 8) !important;
            box-sizing: border-box !important;
            
            .cell-input {
                width: 100% !important;
                max-width: 100% !important;
                box-sizing: border-box !important;
                
                :deep(.v-field) {
                    padding: 0 !important;
                    min-height: auto !important;
                    width: 100% !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                }
                
                :deep(.v-field__input) {
                    padding: 4px 8px !important;
                    min-height: 32px !important;
                    font-size: 0.875rem !important;
                    text-align: center !important; // 데이터 중앙 정렬
                    width: 100% !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                    overflow: hidden !important;
                }
                
                :deep(input) {
                    text-align: center !important; // input 요소 중앙 정렬
                    width: 100% !important;
                    max-width: 100% !important;
                    overflow: hidden !important;
                    text-overflow: ellipsis !important;
                }
                
                :deep(.v-field__outline) {
                    display: none;
                }
            }
        }
        
        .checkbox-header,
        .checkbox-cell {
            // 체크박스 컬럼의 실제 너비를 정확히 30px로 제한
            // box-sizing: border-box를 사용하므로 padding이 너비에 포함됨
            // 최소한의 padding을 유지하여 체크박스가 보이도록 함
            width: 30px !important;
            min-width: 30px !important;
            max-width: 30px !important;
            padding: 4px 0px !important; // 오른쪽 padding을 0으로 하여 첫 번째 컬럼과 거의 붙이도록
            box-sizing: border-box !important;
            // 체크박스가 보이도록 overflow 조정
            overflow: visible !important; // overflow: visible로 변경하여 체크박스가 보이도록
            // 중앙 정렬
            text-align: center;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            
            // v-checkbox 컴포넌트가 보이도록 설정
            :deep(.v-checkbox) {
                width: auto !important;
                min-width: auto !important;
                max-width: none !important;
                margin: 0 !important;
                flex: 0 0 auto !important;
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
            }
            
            :deep(.v-input) {
                width: auto !important;
                min-width: auto !important;
                max-width: none !important;
                margin: 0 !important;
                flex: 0 0 auto !important;
            }
            
            // v-checkbox의 density를 compact로 설정하여 작게 만들기
            :deep(.v-input--density-compact) {
                margin-top: 0 !important;
                margin-bottom: 0 !important;
                padding: 0 !important;
            }
            
            // v-checkbox의 input 요소가 보이도록
            :deep(.v-selection-control) {
                width: auto !important;
                min-width: auto !important;
            }
            
            :deep(.v-selection-control__input) {
                width: auto !important;
                min-width: auto !important;
            }
        }
        
        .file-columns-table-header {
            position: sticky;
            top: 0;
            background-color: rgb(var(--v-theme-surface));
            z-index: 1;
        }
        
        .file-column-header {
            // 체크박스 컬럼을 제외한 나머지 컬럼 너비 계산
            // Actions 컬럼이 있을 때: (전체 너비 - 체크박스 30px - Actions 100px) / 8
            // Actions 컬럼이 없을 때: (전체 너비 - 체크박스 30px) / 8
            padding: 8px 12px;
            font-weight: 500;
            background-color: rgb(var(--v-theme-surface));
            box-sizing: border-box; // padding 포함 너비 계산
            // 텍스트 오버플로우 처리로 컬럼 너비 고정
            // 단, 체크박스 컬럼은 제외
            // 헤더 텍스트가 변경되어도(시스템 컬럼 매핑 시) 컬럼 너비는 고정됨
            // 체크박스 컬럼이 아닌 경우에만 너비 제한 적용
            // 헤더 텍스트가 변경되어도(시스템 컬럼 매핑 시) 컬럼 너비는 고정됨
            &:not(.checkbox-header) {
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
                // 컬럼 너비 강제 고정 - table-layout: fixed와 함께 사용하여 모든 컬럼이 동일한 너비를 가지도록 보장
                // Column9, Column10, Column11, Column12도 Column1-8과 동일한 너비를 가짐
                // 헤더 텍스트가 변경되어도(시스템 컬럼 이름으로 변경되어도) 컬럼 너비는 변하지 않음
                // 인라인 스타일로도 설정했지만, CSS에서도 강제로 고정
                // Actions 컬럼이 있을 때와 없을 때를 구분하여 처리
            }
            
        }
        
        // 체크박스 다음에 오는 첫 번째 컬럼(Column1, Column9)의 왼쪽 padding을 최소화하여 체크박스와 거의 붙이도록
        // 체크박스가 첫 번째이므로, 두 번째 th/td가 첫 번째 데이터 컬럼
        // 모든 규칙을 덮어쓰도록 !important 사용
        thead tr th:nth-child(2):not(.checkbox-header):not(.validation-header) {
            padding-left: 0px !important; // 왼쪽 padding을 0으로 하여 체크박스와 거의 붙이도록
            padding-right: 12px !important; // 오른쪽 padding은 유지
            padding-top: 8px !important; // 상하 padding은 유지
            padding-bottom: 8px !important;
        }
        
        tbody tr td:nth-child(2):not(.checkbox-cell):not(.validation-cell) {
            padding-left: 0px !important; // 왼쪽 padding을 0으로 하여 체크박스와 거의 붙이도록
            padding-right: 12px !important; // 오른쪽 padding은 유지
            padding-top: 8px !important; // 상하 padding은 유지
            padding-bottom: 8px !important;
        }
        
        // editable-cell이 첫 번째 컬럼에 있을 때도 왼쪽 padding을 0으로
        // .editable-cell의 padding: 4px !important를 덮어쓰기 위해 더 구체적인 선택자 사용
        tbody tr td:nth-child(2).editable-cell.file-column-cell {
            padding-left: 0px !important; // .editable-cell의 padding: 4px를 덮어쓰기
            padding-right: 4px !important; // 오른쪽 padding은 유지
            padding-top: 4px !important; // 상하 padding은 유지
            padding-bottom: 4px !important;
        }
        
        .file-column-cell {
            // 체크박스 컬럼을 제외한 나머지 컬럼 너비 계산
            padding: 8px 12px;
            box-sizing: border-box; // padding 포함 너비 계산
            // 텍스트 오버플로우 처리로 컬럼 너비 고정
            // 단, 체크박스 컬럼은 제외
            // 헤더 텍스트가 변경되어도(시스템 컬럼 매핑 시) 컬럼 너비는 고정됨
            &:not(.checkbox-cell) {
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
                // 컬럼 너비 강제 고정 - table-layout: fixed와 함께 사용하여 모든 컬럼이 동일한 너비를 가지도록 보장
                // Column9, Column10, Column11, Column12도 Column1-8과 동일한 너비를 가짐
                // 인라인 스타일로도 설정했지만, CSS에서도 강제로 고정
                // Actions 컬럼이 있을 때와 없을 때를 구분하여 처리
            }
        }
        
        // 기본 너비: Actions 컬럼이 없을 때 (전체 너비 - 체크박스 30px) / 8
        // table-layout: fixed로 모든 컬럼(빈 셀 포함)이 동일한 너비를 가짐
        // 항상 8개의 파일 컬럼 공간을 표시 (실제 컬럼이 4개여도 빈 셀로 8개 공간 유지)
        // 체크박스(30px) + 파일 컬럼 8개 + Actions(있을 때 100px)
        // 테이블의 border가 있지만 border-collapse로 처리되므로 셀 너비에 영향 없음
        // Column1-8과 Column9-12 모두 동일한 너비를 가지도록 보장
        // 헤더 텍스트가 변경되어도(시스템 컬럼 매핑 시) 컬럼 너비는 고정됨
        // 인라인 스타일로 너비를 설정했으므로, CSS는 인라인 스타일이 없을 때를 대비한 보조 설정
        // 하지만 table-layout: fixed를 사용하므로 첫 번째 행(thead)의 셀 너비가 모든 행에 적용됨
        // table-layout: fixed를 사용하므로 첫 번째 행(thead)의 셀 너비가 모든 행에 적용됨
        // 인라인 스타일로 너비를 설정했으므로, CSS는 인라인 스타일을 덮어쓰지 않도록 주의
        // 인라인 스타일이 우선순위가 높으므로, CSS는 box-sizing과 오버플로우만 처리
        thead tr th:not(.checkbox-header):not(.validation-header),
        tbody tr td:not(.checkbox-cell):not(.validation-cell) {
            // 인라인 스타일로 너비를 설정했으므로, CSS는 너비를 설정하지 않음
            // box-sizing과 오버플로우만 처리하여 인라인 스타일의 너비가 제대로 작동하도록 함
            box-sizing: border-box !important;
            // 텍스트 오버플로우 처리 - 긴 텍스트(시스템 컬럼 이름 등)가 컬럼 너비를 넘지 않도록
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
            // 인라인 스타일로 너비를 설정했으므로, CSS는 너비를 설정하지 않음
            // 컬럼 9~12가 컬럼 1~8과 동일한 너비를 가지도록 보장 (인라인 스타일로 처리)
        }
        
        // Actions 컬럼이 있을 때도 동일하게 처리
        // 인라인 스타일로 너비를 설정했으므로, CSS는 보조 설정만 수행
        &.has-validation {
            thead tr th:not(.checkbox-header):not(.validation-header),
            tbody tr td:not(.checkbox-cell):not(.validation-cell) {
                // 인라인 스타일로 너비를 설정했으므로, CSS는 box-sizing과 오버플로우만 처리
                box-sizing: border-box !important;
                // 텍스트 오버플로우 처리
                overflow: hidden !important;
                text-overflow: ellipsis !important;
                white-space: nowrap !important;
                // 인라인 스타일로 너비를 설정했으므로, CSS는 너비를 설정하지 않음
                // 컬럼 9~12가 컬럼 1~8과 동일한 너비를 가지도록 보장 (인라인 스타일로 처리)
            }
        }
        
        // 모든 셀에 box-sizing 적용하여 일관성 유지
        thead tr th,
        tbody tr td {
            box-sizing: border-box !important;
        }
        
        // v-table의 내부 table 요소에 table-layout: fixed 적용
        // 인라인 스타일이 우선순위가 높으므로, CSS는 최소한의 설정만 수행
        :deep(table) {
            table-layout: fixed !important;
            width: 100% !important;
            border-collapse: collapse !important;
        }
        
        // v-table의 내부 thead, tbody 요소에도 스타일 적용
        :deep(thead),
        :deep(tbody) {
            width: 100% !important;
        }
        
        // v-table의 내부 th, td 요소에도 인라인 스타일이 우선 적용되도록 보장
        :deep(th:not(.checkbox-header):not(.validation-header)),
        :deep(td:not(.checkbox-cell):not(.validation-cell)) {
            // 인라인 스타일로 너비를 설정했으므로, CSS는 너비를 설정하지 않음
            // box-sizing과 오버플로우만 처리
            box-sizing: border-box !important;
            overflow: hidden !important;
            text-overflow: ellipsis !important;
            white-space: nowrap !important;
        }
    }
    
    .columns-section {
        :deep(.v-select) {
            .v-field {
                min-width: 0;
            }
        }
    }
}
</style>




